/**
 * ThemeContext: Light / Dark / Auto theme. Persists to localStorage; applies class to document (Tailwind darkMode: 'class').
 * Auto follows system prefers-color-scheme. On LOGOUT_EVENT, forces light mode and persists 'light' so logged-out UI defaults to light.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'theme-preference';

/** Dispatched by AuthContext on logout so user preferences (e.g. theme) are cleared from UI and storage. */
export const LOGOUT_EVENT = 'app-logout';

function getStoredTheme() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'auto') return v;
  } catch (_) {}
  return 'auto';
}

function getSystemDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyResolved(isDark) {
  const root = document.documentElement;
  if (isDark) {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

const ThemeContext = createContext(null);

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getStoredTheme);
  const [systemDark, setSystemDark] = useState(getSystemDark);

  // On logout: default to light mode (logged-out experience must not follow prior dark choice or system dark).
  useEffect(() => {
    const handleLogout = () => {
      try {
        localStorage.setItem(STORAGE_KEY, 'light');
      } catch (_) {}
      setThemeState('light');
      applyResolved(false);
    };
    window.addEventListener(LOGOUT_EVENT, handleLogout);
    return () => window.removeEventListener(LOGOUT_EVENT, handleLogout);
  }, []);

  const resolvedDark = theme === 'dark' || (theme === 'auto' && systemDark);
  useEffect(() => {
    applyResolved(resolvedDark);
  }, [resolvedDark]);

  useEffect(() => {
    if (theme !== 'auto') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setSystemDark(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  const setTheme = useCallback((value) => {
    if (value !== 'light' && value !== 'dark' && value !== 'auto') return;
    setThemeState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (_) {}
  }, []);

  const value = { theme, setTheme, resolvedDark };
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export { getStoredTheme, getSystemDark, applyResolved, STORAGE_KEY };
